import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import UserProfile from "./UserProfile";
import { OverwolfGoVipSummary } from "@/pages/overwolf/components/OverwolfGoVipSummary";
import DevourPlayStats from "../play/DevourPlayStats";
import FrameButton from "@/components/buttons/FrameButton";
import { IStore } from "@/redux/defaultStore";
// Removed useGetOverwolfSummary - now receiving data as props
import "./ProfileCard.scss";

interface ProfileCardProps {
    fuelBalance: string;
    userProfile?: any; // GetUserResponse type
    accountLevel?: any; // AccountLevel type
    overwolfSummary?: any; // GetUserSummaryResponse type
    isAuthenticated: boolean;
    isLoading?: boolean;
    onLoginClick?: () => void;
    onLevelsClick?: () => void;
    onBadgeClick?: () => void;
    onLoadFuelClick?: () => void; // Optional handler for Load Fuel action
    forceMobileView?: boolean; // Force mobile view for compact layouts
    noCardStyling?: boolean; // Remove card background/border for embedded use
    cockpitView?: boolean; // Optimize layout for cockpit me-column
}

/**
 * ProfileCard Component
 * Displays user profile information including level, XP, stats, and fuel balance
 */
const ProfileCard: React.FC<ProfileCardProps> = ({
    fuelBalance,
    userProfile,
    accountLevel,
    overwolfSummary,
    isAuthenticated,
    isLoading = false,
    onLoginClick,
    onLevelsClick,
    onBadgeClick,
    onLoadFuelClick,
    forceMobileView,
    noCardStyling = false,
    cockpitView = false,
}) => {
    const navigate = useNavigate();
    const isOnOverwolf = useSelector((store: IStore) => store.metaStore.isOnOverwolf);
    // Use passed prop instead of fetching again
    const summaryData = overwolfSummary;
    const overwolfAppUrl = "https://www.overwolf.com/app/Devour-DevourPlay";

    const handleAddFuel = () => {
        if (onLoadFuelClick) {
            onLoadFuelClick();
        } else {
            navigate("/load-fuel");
        }
    };

    const handleDownloadClick = () => {
        window.open(overwolfAppUrl, "_blank", "noopener,noreferrer");
    };

    const handleLoginClick = () => {
        if (onLoginClick) {
            onLoginClick();
        }
    };

    if (isLoading) {
        return (
            <div className="profile-card profile-card--loading">
                <div className="profile-card__skeleton">
                    <div className="skeleton-avatar" />
                    <div className="skeleton-content">
                        <div className="skeleton-line skeleton-line--thick" />
                        <div className="skeleton-line skeleton-line--thin" />
                        <div className="skeleton-line skeleton-line--progress" />
                    </div>
                    <div className="skeleton-fuel" />
                </div>
            </div>
        );
    }

    return (
        <div className={`profile-card ${!isAuthenticated ? "profile-card--unauthenticated" : ""} ${forceMobileView ? "profile-card--force-mobile" : ""} ${noCardStyling ? "profile-card--no-card" : ""} ${cockpitView ? "profile-card--cockpit" : ""}`}>
            {/* Left section wrapper for proper grid layout */}
            <div className="profile-card__left-section">
                {/* Level and XP Progress - Only render for authenticated users */}
                {isAuthenticated &&
                    <UserProfile
                        userProfile={userProfile}
                        accountLevel={accountLevel}
                        onLevelsClick={onLevelsClick}
                        onBadgeClick={onBadgeClick}
                        forceMobileView={forceMobileView}
                        dataCard={forceMobileView} // Use data card layout when forcing mobile view
                        cockpitView={cockpitView} // Pass cockpit view prop
                    />
                }

                {/* Fuel Balance - Only render for authenticated users */}
                {isAuthenticated && (
                    <div
                        className={`profile-card__fuel-balance ${forceMobileView ? 'profile-card__fuel-balance--games' : ''}`}
                        onClick={handleAddFuel}
                        role="button"
                        tabIndex={0}
                        onKeyPress={(e) => e.key === 'Enter' && handleAddFuel()}
                        style={{ cursor: 'pointer' }}
                    >
                        <img
                            className="fuel-icon"
                            src={`${import.meta.env.VITE_CDN_URL}/images/FUEL.webp`}
                            alt="Fuel"
                        />
                        <span className="fuel-amount">${fuelBalance}</span>
                        <button
                            className="fuel-add"
                            onClick={(e) => {
                                e.stopPropagation(); // Prevent double-firing
                                handleAddFuel();
                            }}
                            aria-label="Add Fuel"
                        >
                            +
                        </button>
                    </div>
                )}
            </div>

            {/* Login/Download CTA for non-authenticated users */}
            {!isAuthenticated &&
        <div className="profile-card__auth-cta">
            <img
                className="auth-cta__logo"
                src={`${import.meta.env.VITE_CDN_URL}/images/devourplay-logo2.webp`}
                alt="DevourPlay"
            />
            <p className="auth-cta__message">
                Game On. Loot Up.<br />
                Grab DevourPlay and dive in.
            </p>
            <div className="auth-cta__buttons">
                <FrameButton
                    onClick={handleLoginClick}
                    className="auth-cta__login-btn"
                    size="normal"
                >
                    Login or SignUp
                </FrameButton>
                <FrameButton
                    color="white-outline"
                    size="normal"
                    onClick={handleDownloadClick}
                    className="auth-cta__download-btn"
                >
                    Download DevourPlay
                </FrameButton>
            </div>
        </div>
            }

            {/* Stats Summary - Only render for authenticated users with real data */}
            {isAuthenticated && summaryData &&
                <div className="profile-card__stats-container">
                    <DevourPlayStats
                        earned={summaryData.totalXp || 0}
                        playtime={(() => {
                            const totalMinutes = summaryData.totalTime || 0;
                            const hours = Math.floor(totalMinutes / 60);
                            const minutes = totalMinutes % 60;
                            if (hours === 0) return `${minutes}m`;
                            if (minutes === 0) return `${hours}h`;
                            return `${hours}h ${minutes}m`;
                        })()}
                        gamesPlayed={summaryData.totalGames || 0}
                        forceMobileView={forceMobileView}
                    />
                </div>
            }

        </div>
    );
};

export default React.memo(ProfileCard);